import type { FormEvent, ReactNode } from 'react';
import type { GitRepositories } from './useGitRepositories';
import { BitbucketIcon, GithubIcon, GitlabIcon } from './icons';
import { Callout, Dialog, Field, Input, Tabs, Textarea } from '../primitives';

/**
 * GitConnectModal connects a Git provider for the repository selector: the
 * organization's GitHub App (existing, or registered through GitHub's
 * manifest flow) or a personal access token / app password. Shared by the
 * Magento and WordPress onboarding pages.
 */
export function GitConnectModal({ git }: { git: GitRepositories }) {
  const {
    connectModalOpen,
    setConnectModalOpen,
    modalProvider,
    modalAccount,
    setModalAccount,
    modalToken,
    setModalToken,
    modalConnecting,
    modalError,
    setModalError,
    githubConnectTab,
    setGithubConnectTab,
    ghAppId,
    setGhAppId,
    ghAppName,
    setGhAppName,
    ghClientId,
    setGhClientId,
    ghClientSecret,
    setGhClientSecret,
    ghWebhookSecret,
    setGhWebhookSecret,
    ghPrivateKey,
    setGhPrivateKey,
    ghInstallationId,
    setGhInstallationId,
    ghAccount,
    setGhAccount,
    ghOrgName,
    setGhOrgName,
    handleSaveProviderConnection,
    handleSaveManualGitHubApp,
    handleStartManifestFlowFromModal,
  } = git;
  const providerName = modalProvider === 'github' ? 'GitHub' : modalProvider === 'gitlab' ? 'GitLab' : 'Bitbucket';
  const tab = modalProvider === 'github' ? githubConnectTab : 'token';
  const close = () => setConnectModalOpen(false);
  const cancel = (
    <button type="button" className="button button--secondary" onClick={close} disabled={modalConnecting}>
      Cancel
    </button>
  );

  let onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  let primary: ReactNode;
  const appIncomplete =
    !ghAppId.trim() || !ghInstallationId.trim() || !ghAccount.trim() || !ghClientSecret.trim() || !ghWebhookSecret.trim() || !ghPrivateKey.trim();
  if (tab === 'app') {
    onSubmit = handleSaveManualGitHubApp;
    primary = (
      <button
        type="submit"
        className="button button--primary"
        disabled={modalConnecting || appIncomplete}
        title={appIncomplete ? 'Fill in every field that is not marked optional.' : undefined}
      >
        {modalConnecting ? 'Connecting the app' : 'Connect GitHub App'}
      </button>
    );
  } else if (tab === 'manifest') {
    onSubmit = (event) => {
      event.preventDefault();
      void handleStartManifestFlowFromModal();
    };
    primary = (
      <button type="submit" className="button button--primary" disabled={modalConnecting}>
        {modalConnecting ? 'Redirecting' : 'Register GitHub App'}
      </button>
    );
  } else {
    onSubmit = handleSaveProviderConnection;
    primary = (
      <button type="submit" className="button button--primary" disabled={modalConnecting}>
        {modalConnecting ? 'Verifying' : 'Save connection'}
      </button>
    );
  }

  return (
    <Dialog
      open={connectModalOpen}
      onClose={close}
      busy={modalConnecting}
      onSubmit={onSubmit}
      title={
        <span className="heading-with-icon">
          {modalProvider === 'github' ? <GithubIcon size={20} /> : modalProvider === 'gitlab' ? <GitlabIcon size={20} /> : <BitbucketIcon size={20} />}
          <span>Connect {providerName}</span>
        </span>
      }
      footer={
        <>
          {cancel}
          {primary}
        </>
      }
      testId="git-connect-dialog"
    >
      {modalProvider === 'github' ? (
        <Tabs
          label="GitHub connection method"
          variant="secondary"
          value={githubConnectTab}
          onChange={(next) => {
            setGithubConnectTab(next);
            setModalError(null);
          }}
          options={[
            { value: 'app', label: 'Existing GitHub App' },
            { value: 'token', label: 'Personal access token' },
            { value: 'manifest', label: 'New GitHub App' },
          ]}
        />
      ) : null}

      {modalError ? (
        <Callout tone="danger" role="alert">
          {modalError}
        </Callout>
      ) : null}

      {tab === 'app' ? (
        <div className="stack">
          <div className="form-grid form-grid--2">
            <Field label="App ID">
              <Input id="gh-app-id" type="text" required placeholder="123456" value={ghAppId} onChange={(e) => setGhAppId(e.target.value)} disabled={modalConnecting} />
            </Field>
            <Field label="App name" optional>
              <Input id="gh-app-name" type="text" placeholder="my-commerce-ops-app" value={ghAppName} onChange={(e) => setGhAppName(e.target.value)} disabled={modalConnecting} />
            </Field>
            <Field label="Client ID" optional>
              <Input id="gh-client-id" type="text" placeholder="Iv1.abc123..." value={ghClientId} onChange={(e) => setGhClientId(e.target.value)} disabled={modalConnecting} />
            </Field>
            <Field label="Installation ID">
              <Input id="gh-installation-id" type="text" required placeholder="12345678" value={ghInstallationId} onChange={(e) => setGhInstallationId(e.target.value)} disabled={modalConnecting} />
            </Field>
          </div>
          <Field label="Account or organization">
            <Input id="gh-account" type="text" required placeholder="my-org or username" value={ghAccount} onChange={(e) => setGhAccount(e.target.value)} disabled={modalConnecting} />
          </Field>
          <div className="form-grid form-grid--2">
            <Field label="Client secret">
              <Input id="gh-client-secret" type="password" required placeholder="Client secret" value={ghClientSecret} onChange={(e) => setGhClientSecret(e.target.value)} disabled={modalConnecting} autoComplete="off" />
            </Field>
            <Field label="Webhook secret">
              <Input id="gh-webhook-secret" type="password" required placeholder="Webhook secret" value={ghWebhookSecret} onChange={(e) => setGhWebhookSecret(e.target.value)} disabled={modalConnecting} autoComplete="off" />
            </Field>
          </div>
          <Field label="Private key (PEM)">
            <Textarea
              id="gh-private-key"
              className="font-mono"
              required
              placeholder={'-----BEGIN RSA PRIVATE KEY-----\n...\n-----END RSA PRIVATE KEY-----'}
              value={ghPrivateKey}
              onChange={(e) => setGhPrivateKey(e.target.value)}
              disabled={modalConnecting}
              rows={4}
            />
          </Field>
        </div>
      ) : tab === 'manifest' ? (
        <div className="stack">
          <p className="type-description">
            Registers and installs a new GitHub App with the repository permissions Commerce Ops needs. GitHub opens so you can approve it.
          </p>
          <Field
            label="GitHub organization"
            optional
            hint="Leave it empty to register the app on your personal account."
          >
            <Input id="gh-org-name" type="text" placeholder="e.g. acme-corp" value={ghOrgName} onChange={(e) => setGhOrgName(e.target.value)} disabled={modalConnecting} />
          </Field>
        </div>
      ) : (
        <div className="stack">
          <p className="type-description">
            {modalProvider === 'github'
              ? 'A personal access token (classic or fine-grained) that can read the repositories.'
              : 'An access token or app password that can read the repositories.'}
          </p>
          <Field label={modalProvider === 'github' ? 'Account' : 'Account or workspace'}>
            <Input id="git-account" type="text" required value={modalAccount} onChange={(e) => setModalAccount(e.target.value)} placeholder="e.g. acme-agency" disabled={modalConnecting} />
          </Field>
          <Field
            label={modalProvider === 'bitbucket' ? 'App password' : 'Access token'}
            hint="Encrypted before it is stored; never kept in plain text."
          >
            <Input
              id="git-token"
              type="password"
              value={modalToken}
              onChange={(e) => setModalToken(e.target.value)}
              placeholder={modalProvider === 'github' ? 'ghp_...' : modalProvider === 'gitlab' ? 'glpat-...' : 'App password'}
              disabled={modalConnecting}
              autoComplete="off"
            />
          </Field>
        </div>
      )}
    </Dialog>
  );
}
